<template>
  <div class="max-w-6xl mx-auto">
    <!-- Page Header -->
    <div class="text-center mb-8">
      <h1 class="text-3xl font-bold mb-4">My Competitions</h1>
      <p class="text-base-content/70 max-w-2xl mx-auto">
        Manage your competitions and review join requests from participants.
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-error mb-8">
      <i class="mdi mdi-alert-circle h-6 w-6"></i>
      <span>{{ error }}</span>
    </div>

    <!-- No Competitions -->
    <div v-else-if="myCompetitions.length === 0" class="text-center py-12">
      <div class="max-w-md mx-auto">
        <div class="text-6xl mb-4">🏆</div>
        <h4 class="text-lg font-semibold mb-2">No competitions yet</h4>
        <p class="text-base-content/70 mb-6">
          Create your first competition to get started.
        </p>
        <router-link to="/create" class="btn btn-primary">
          Create Competition
        </router-link>
      </div>
    </div>

    <!-- Competitions List -->
    <div v-else class="space-y-6">
      <div 
        v-for="competition in myCompetitions" 
        :key="competition.id" 
        class="relative"
      >
        <!-- Competition Header -->
        <CompetitionHeader 
          :competition="competition"
          :participant-count="competition.participantCount"
          :show-delete-button="true"
          :loading="competition.loadingCount"
          @delete="confirmDelete(competition)"
        />
        
        <!-- Competition Management Actions -->
        <div class="card bg-base-100 card-bordered mt-4">
          <div class="card-body p-4">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-4">
                <!-- Competition Stats -->
                <div class="flex items-center gap-6 text-sm">
                  <div class="flex items-center gap-2">
                    <i class="mdi mdi-chart-line text-primary"></i>
                    <span>{{ competition.required_stock_count }} stocks</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <i class="mdi mdi-weight text-success"></i>
                    <span :class="competition.allow_weights ? 'text-success' : 'text-base-content/70'">
                      {{ competition.allow_weights ? 'Weighted' : 'Equal Weight' }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <i class="mdi mdi-account-group text-info"></i>
                    <span>{{ competition.privacy_mode }}</span>
                  </div>
                </div>
                
                <!-- Pending Requests Badge -->
                <div v-if="competition.privacy_mode === 'private'" class="flex items-center gap-2">
                  <i class="mdi mdi-account-clock text-warning"></i>
                  <span class="text-sm">
                    {{ competition.pending_requests_count || 0 }} pending request{{ (competition.pending_requests_count || 0) !== 1 ? 's' : '' }}
                  </span>
                  <router-link 
                    v-if="(competition.pending_requests_count || 0) > 0"
                    :to="`/moderate/${competition.id}`" 
                    class="btn btn-warning btn-xs"
                  >
                    Review
                  </router-link>
                </div>
              </div>
              
              <!-- Action Buttons -->
              <div class="flex gap-2">
                <router-link 
                  :to="`/competition/${competition.id}`" 
                  class="btn btn-ghost btn-sm"
                >
                  <i class="mdi mdi-eye"></i>
                  View Details
                </router-link>
                <router-link 
                  v-if="competition.privacy_mode === 'private'"
                  :to="`/moderate/${competition.id}`" 
                  class="btn btn-primary btn-sm"
                >
                  <i class="mdi mdi-shield-check"></i>
                  Moderate
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal modal-open">
      <div class="modal-box">
        <h3 class="font-bold text-lg">Confirm Deletion</h3>
        <p class="py-4">
          Are you sure you want to delete the competition 
          <strong>"{{ competitionToDelete?.name }}"</strong>?
          <br><br>
          This action cannot be undone and will delete all entries and stock picks associated with this competition.
        </p>
        <div class="modal-action">
          <button @click="deleteCompetition" class="btn btn-error" :disabled="deleting">
            <span v-if="deleting" class="loading loading-spinner loading-xs"></span>
            Delete Competition
          </button>
          <button @click="cancelDelete" class="btn btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useCompetitionStore } from '../stores/competition'
import CompetitionHeader from '../components/CompetitionHeader.vue'

const competitionStore = useCompetitionStore()

const loading = ref(false)
const error = ref('')
const myCompetitions = ref([])
const showDeleteModal = ref(false)
const competitionToDelete = ref(null)
const deleting = ref(false)

const fetchMyCompetitions = async () => {
  loading.value = true
  error.value = ''
  
  try {
    const competitions = await competitionStore.fetchMyCompetitions()
    
    // Fetch participant counts for each competition
    const competitionsWithCounts = await Promise.all(
      competitions.map(async (competition) => {
        try {
          const leaderboard = await competitionStore.fetchLeaderboard(competition.id)
          return {
            ...competition,
            participantCount: leaderboard?.entries?.length || 0,
            loadingCount: false
          }
        } catch (err) {
          console.warn(`Failed to fetch participant count for competition ${competition.id}:`, err)
          return {
            ...competition,
            participantCount: 0,
            loadingCount: false
          }
        }
      })
    )
    
    myCompetitions.value = competitionsWithCounts
  } catch (err) {
    error.value = err.message || 'Failed to load competitions'
  } finally {
    loading.value = false
  }
}

const confirmDelete = (competition) => {
  competitionToDelete.value = competition
  showDeleteModal.value = true
}

const deleteCompetition = async () => {
  if (!competitionToDelete.value) return
  
  deleting.value = true
  try {
    await competitionStore.deleteCompetition(competitionToDelete.value.id)
    showDeleteModal.value = false
    competitionToDelete.value = null
    // Refresh the list
    await fetchMyCompetitions()
  } catch (err) {
    error.value = err.message || 'Failed to delete competition'
  } finally {
    deleting.value = false
  }
}

const cancelDelete = () => {
  showDeleteModal.value = false
  competitionToDelete.value = null
}

onMounted(() => {
  fetchMyCompetitions()
})
</script> 